import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Component from '../../components/Component';
import {
  assignDiscoverSectionVideos,
  createDiscoverSection,
  createDiscoverSectionMultipart,
  getDiscoverSections,
  getDiscoverSectionsPayload,
  isDiscoverSectionsApiSuccess,
  previewDiscover,
  setDiscoverSectionEnabled,
  updateDiscoverSection,
  updateDiscoverSectionMultipart,
  uploadDiscoverSectionImage,
} from '../../services/discoverSectionsApi';
import {
  getAdminFanVideos,
  getFanVideoApiPayload,
  isFanVideoApiSuccess,
} from '../../services/fanVideoApi';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Add, Edit } from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

const SECTION_CODE_OPTIONS = [
  { value: 'daily_digest', label: 'Daily Digest' },
  { value: 'fan_stories', label: 'Fan Stories / IPL Recap' },
  { value: 'dugout_daily', label: 'Dugout Daily' },
  { value: 'whats_new', label: "What's New" },
  { value: 'promotion', label: 'Promotion' },
];

const VIDEO_BACKED_SECTION_CODES = ['promotion', 'dugout_daily', 'whats_new'];

const SCHEDULE_STATUS_COLORS = {
  live: 'success',
  disabled: 'default',
  scheduled: 'info',
  expired: 'warning',
};

const emptyForm = {
  sectionCode: 'daily_digest',
  title: '',
  imageUrl: '',
  directionUrl: '',
  enabled: true,
  sortOrder: '0',
  competitionCid: '',
  startsAt: '',
  endsAt: '',
  metadata: '{}',
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
};

const toDateInputValue = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const local = new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const fromDateInputValue = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
};

const getValue = (item, camelKey, snakeKey) => item?.[camelKey] ?? item?.[snakeKey];

const normalizeSections = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.sections)) return payload.sections;
  if (Array.isArray(payload?.rows)) return payload.rows;
  return [];
};

const normalizeVideoAssets = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.videos)) return payload.videos;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.assets)) return payload.assets;
  if (Array.isArray(payload?.fanVideos)) return payload.fanVideos;
  if (Array.isArray(payload?.rows)) return payload.rows;
  return [];
};

const getAssetValue = (asset, camelKey, snakeKey) => asset?.[camelKey] ?? asset?.[snakeKey];
const getAssetType = (asset) => asset?.type || getAssetValue(asset, 'videoType', 'video_type') || 'promotional';

const isVideoBackedSection = (sectionCode) => VIDEO_BACKED_SECTION_CODES.includes(sectionCode);

const getAssignedVideoIds = (section) => {
  if (Array.isArray(section?.videoIds)) return section.videoIds;
  if (Array.isArray(section?.video_ids)) return section.video_ids;
  if (Array.isArray(section?.videos)) {
    return section.videos.map((video) => video.id).filter((id) => id != null);
  }
  return [];
};

const getAssignedVideos = (section) => {
  if (Array.isArray(section?.videos)) return section.videos;
  return [];
};

const getLiveVideoCount = (section) => {
  const liveCount = getValue(section, 'liveVideoCount', 'live_video_count');
  if (liveCount != null && liveCount !== '') return Number(liveCount);
  return getAssignedVideos(section).filter((video) => video?.appVisible === true || video?.scheduleStatus === 'live').length;
};

const getScheduleStatus = (video) => {
  const status = video?.scheduleStatus || video?.schedule_status;
  if (status) return String(status).toLowerCase();
  if (video?.enabled === false) return 'disabled';
  if (video?.appVisible) return 'live';
  return 'disabled';
};

const getDirectionUrl = (section) => {
  if (!section) return '';
  const topLevel =
    section.directionUrl
    || section.direction_url
    || section.navigationLink
    || section.navigation_link
    || section.redirectionUrl
    || section.redirection_url
    || section.redirect_url
    || section.link
    || '';
  if (typeof topLevel === 'string' && topLevel.trim()) return topLevel.trim();

  const metadata = getValue(section, 'metadata', 'metadata') || {};
  const nestedLink =
    metadata.navigation && typeof metadata.navigation === 'object'
      ? metadata.navigation.link
      : undefined;
  const flatLink = metadata.navigation_link;
  const configured =
    (typeof nestedLink === 'string' && nestedLink.trim())
    || (typeof flatLink === 'string' && flatLink.trim())
    || (typeof metadata.link === 'string' && metadata.link.trim())
    || '';
  return configured;
};

/** Keep Direction URL field as the single edit surface; strip mirrored nav keys from metadata JSON. */
const stripDirectionFromMetadata = (metadata) => {
  const next = { ...(metadata && typeof metadata === 'object' ? metadata : {}) };
  delete next.navigation_link;
  delete next.link;
  if (next.navigation && typeof next.navigation === 'object' && !Array.isArray(next.navigation)) {
    const nav = { ...next.navigation };
    delete nav.link;
    if (Object.keys(nav).length === 0) delete next.navigation;
    else next.navigation = nav;
  }
  return next;
};

const applyDirectionUrlToMetadata = (metadata, directionUrl) => {
  const next = stripDirectionFromMetadata(metadata);
  const trimmed = typeof directionUrl === 'string' ? directionUrl.trim() : '';
  if (!trimmed) return next;
  next.navigation_link = trimmed;
  const nav =
    next.navigation && typeof next.navigation === 'object' && !Array.isArray(next.navigation)
      ? { ...next.navigation }
      : {};
  nav.link = trimmed;
  next.navigation = nav;
  return next;
};

const formatAssignedVideosSummary = (section) => {
  const sectionCode = getValue(section, 'sectionCode', 'section_code');
  if (!isVideoBackedSection(sectionCode)) return null;
  const ids = getAssignedVideoIds(section);
  const total = ids.length || getAssignedVideos(section).length;
  if (total === 0) return { label: 'None', liveCount: 0, total: 0, videos: [] };
  const liveCount = getLiveVideoCount(section);
  return {
    label: `${liveCount}/${total} live`,
    liveCount,
    total,
    videos: getAssignedVideos(section),
  };
};

const isValidDirectionUrl = (value) => {
  if (!value) return true;
  const trimmed = value.trim();
  if (trimmed.startsWith('/')) return true;
  if (trimmed.startsWith('dugout://')) return true;
  try {
    const parsed = new URL(trimmed);
    return ['http:', 'https:'].includes(parsed.protocol);
  } catch {
    return false;
  }
};

const isValidUrl = (value) => {
  if (!value) return true;
  try {
    const parsed = new URL(value);
    return ['http:', 'https:'].includes(parsed.protocol);
  } catch {
    return false;
  }
};

function DiscoverSections({ embedded = false }) {
  const [sections, setSections] = useState([]);
  const [globalVideos, setGlobalVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);
  const [selectedVideoIds, setSelectedVideoIds] = useState([]);
  const [competitionFilter, setCompetitionFilter] = useState('');
  const [previewCompetitionCid, setPreviewCompetitionCid] = useState('129908');
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleSessionExpired = useCallback(
    (res) => {
      if (String(res?.statusCode || '') === '10003') {
        showSnackbar('Session expired. Please login again.', 'error');
        setTimeout(() => {
          window.location = '/login';
        }, 1500);
        return true;
      }
      return false;
    },
    [showSnackbar]
  );

  const loadGlobalVideos = useCallback(async () => {
    const res = await getAdminFanVideos();
    if (handleSessionExpired(res)) return;
    if (!isFanVideoApiSuccess(res)) return;
    setGlobalVideos(normalizeVideoAssets(getFanVideoApiPayload(res)));
  }, [handleSessionExpired]);

  const loadSections = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getDiscoverSections({ competitionCid: competitionFilter.trim() || undefined });
      if (handleSessionExpired(res)) return;
      if (!isDiscoverSectionsApiSuccess(res)) {
        setSections([]);
        showSnackbar(res?.message || 'Failed to load discover sections', 'error');
        return;
      }
      setSections(normalizeSections(getDiscoverSectionsPayload(res)));
    } finally {
      setLoading(false);
    }
  }, [competitionFilter, handleSessionExpired, showSnackbar]);

  useEffect(() => {
    loadSections();
    loadGlobalVideos();
  }, [loadSections, loadGlobalVideos]);

  const stats = useMemo(() => {
    const enabled = sections.filter((section) => Boolean(getValue(section, 'enabled', 'enabled'))).length;
    const competitionOverrides = sections.filter((section) => getValue(section, 'competitionCid', 'competition_cid') != null).length;
    return { total: sections.length, enabled, competitionOverrides };
  }, [sections]);

  const selectableGlobalVideos = useMemo(() => globalVideos, [globalVideos]);

  const selectedVideoOptions = useMemo(
    () => selectableGlobalVideos.filter((asset) => selectedVideoIds.includes(asset.id)),
    [selectableGlobalVideos, selectedVideoIds]
  );

  /** Assigned videos from the editing section, used for scheduleStatus badges in the form. */
  const editingAssignedVideosById = useMemo(() => {
    const map = new Map();
    getAssignedVideos(editingSection).forEach((video) => {
      if (video?.id != null) map.set(video.id, video);
    });
    return map;
  }, [editingSection]);

  const resetVideoSelection = (videoIds = []) => {
    setSelectedVideoIds(videoIds);
  };

  const updateForm = (field, value) => {
    if (field === 'sectionCode') {
      setFormData((prev) => ({ ...prev, [field]: value }));
      resetVideoSelection([]);
      return;
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleOpenCreate = () => {
    setEditingSection(null);
    setFormData(emptyForm);
    setImageFile(null);
    resetVideoSelection([]);
    setDialogOpen(true);
  };

  const handleOpenEdit = (section) => {
    const metadata = getValue(section, 'metadata', 'metadata') || {};
    const assignedIds = getAssignedVideoIds(section);
    setEditingSection(section);
    setImageFile(null);
    setFormData({
      sectionCode: getValue(section, 'sectionCode', 'section_code') || 'daily_digest',
      title: getValue(section, 'title', 'title') || '',
      imageUrl: getValue(section, 'imageUrl', 'image_url') || '',
      directionUrl: getDirectionUrl(section),
      enabled: Boolean(getValue(section, 'enabled', 'enabled')),
      sortOrder: String(getValue(section, 'sortOrder', 'sort_order') ?? 0),
      competitionCid: getValue(section, 'competitionCid', 'competition_cid') != null
        ? String(getValue(section, 'competitionCid', 'competition_cid'))
        : '',
      startsAt: toDateInputValue(getValue(section, 'startsAt', 'starts_at')),
      endsAt: toDateInputValue(getValue(section, 'endsAt', 'ends_at')),
      metadata: JSON.stringify(stripDirectionFromMetadata(metadata), null, 2),
    });
    resetVideoSelection(assignedIds);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSection(null);
    setFormData(emptyForm);
    setImageFile(null);
    resetVideoSelection([]);
  };

  const buildPayload = () => {
    const sortOrder = Number(formData.sortOrder || 0);
    const competitionCid = formData.competitionCid.trim() ? Number(formData.competitionCid) : null;
    const directionUrl = formData.directionUrl.trim();
    const metadata = applyDirectionUrlToMetadata(
      formData.metadata.trim() ? JSON.parse(formData.metadata) : {},
      directionUrl,
    );
    const payload = {
      sectionCode: formData.sectionCode,
      title: formData.title.trim(),
      imageUrl: formData.imageUrl.trim(),
      directionUrl: directionUrl || null,
      enabled: !!formData.enabled,
      sortOrder: Number.isFinite(sortOrder) ? sortOrder : 0,
      competitionCid: Number.isFinite(competitionCid) ? competitionCid : null,
      startsAt: fromDateInputValue(formData.startsAt),
      endsAt: fromDateInputValue(formData.endsAt),
      metadata,
    };
    if (isVideoBackedSection(formData.sectionCode)) {
      payload.videoIds = selectedVideoIds;
      payload.makeLive = true;
    }
    return payload;
  };

  const buildMultipartFormData = (payload) => {
    const body = new FormData();
    body.append('sectionCode', payload.sectionCode);
    body.append('title', payload.title);
    body.append('enabled', String(payload.enabled));
    body.append('sortOrder', String(payload.sortOrder));
    body.append('metadata', JSON.stringify(payload.metadata || {}));
    if (payload.imageUrl) body.append('imageUrl', payload.imageUrl);
    if (payload.directionUrl) body.append('directionUrl', payload.directionUrl);
    else body.append('directionUrl', '');
    if (payload.competitionCid != null) body.append('competitionCid', String(payload.competitionCid));
    if (payload.startsAt) body.append('startsAt', payload.startsAt);
    if (payload.endsAt) body.append('endsAt', payload.endsAt);
    if (Array.isArray(payload.videoIds)) {
      body.append('videoIds', JSON.stringify(payload.videoIds));
      body.append('makeLive', String(payload.makeLive !== false));
    }
    if (imageFile) body.append('image', imageFile);
    return body;
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      showSnackbar('Title is required', 'warning');
      return false;
    }
    if (!formData.imageUrl.trim() && !imageFile) {
      showSnackbar('Image URL or image upload is required', 'warning');
      return false;
    }
    if (formData.imageUrl.trim() && !isValidUrl(formData.imageUrl.trim())) {
      showSnackbar('Image URL must be a valid http(s) URL', 'warning');
      return false;
    }
    if (formData.directionUrl.trim() && !isValidDirectionUrl(formData.directionUrl.trim())) {
      showSnackbar('Direction URL must be /path, dugout://…, or http(s)', 'warning');
      return false;
    }
    if (formData.competitionCid.trim()) {
      const competitionCid = Number(formData.competitionCid);
      if (!Number.isInteger(competitionCid) || competitionCid <= 0) {
        showSnackbar('Competition CID must be a positive integer', 'warning');
        return false;
      }
    }
    try {
      JSON.parse(formData.metadata.trim() || '{}');
    } catch {
      showSnackbar('Metadata must be valid JSON', 'warning');
      return false;
    }
    const startsAt = fromDateInputValue(formData.startsAt);
    const endsAt = fromDateInputValue(formData.endsAt);
    if (startsAt && endsAt && new Date(startsAt) > new Date(endsAt)) {
      showSnackbar('Start time must be before end time', 'warning');
      return false;
    }
    return true;
  };

  const shouldAssignVideos = () => isVideoBackedSection(formData.sectionCode);

  const warnIfVideosNotFullyLive = (sectionPayload) => {
    if (!sectionPayload || !isVideoBackedSection(formData.sectionCode)) return false;
    const assignedIds = getAssignedVideoIds(sectionPayload);
    const total = assignedIds.length || selectedVideoIds.length;
    if (total === 0) return false;
    const liveCount = getLiveVideoCount(sectionPayload);
    if (liveCount < total) {
      showSnackbar(
        `Saved, but only ${liveCount}/${total} assigned videos are live (some may still be disabled)`,
        'warning'
      );
      return true;
    }
    return false;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    const payload = buildPayload();
    setSaving(true);
    try {
      const res = imageFile
        ? editingSection
          ? await updateDiscoverSectionMultipart(editingSection.id, buildMultipartFormData(payload))
          : await createDiscoverSectionMultipart(buildMultipartFormData(payload))
        : editingSection
          ? await updateDiscoverSection(editingSection.id, payload)
          : await createDiscoverSection(payload);
      if (handleSessionExpired(res)) return;
      if (!isDiscoverSectionsApiSuccess(res)) {
        showSnackbar(res?.message || 'Failed to save discover section', 'error');
        return;
      }

      const savedSection = getDiscoverSectionsPayload(res);
      const sectionId = editingSection?.id ?? savedSection?.id;
      let finalSection = savedSection;

      // Explicit assign keeps videoIds in sync (including clearing) with makeLive: true.
      if (sectionId && shouldAssignVideos()) {
        const assignRes = await assignDiscoverSectionVideos(sectionId, selectedVideoIds, { makeLive: true });
        if (handleSessionExpired(assignRes)) return;
        if (!isDiscoverSectionsApiSuccess(assignRes)) {
          showSnackbar(assignRes?.message || 'Section saved but video assignment failed', 'error');
          loadSections();
          return;
        }
        finalSection = getDiscoverSectionsPayload(assignRes) || savedSection;
      }

      if (!warnIfVideosNotFullyLive(finalSection)) {
        showSnackbar(editingSection ? 'Discover section updated' : 'Discover section created');
      }
      handleCloseDialog();
      loadSections();
    } finally {
      setSaving(false);
    }
  };

  const handleUploadImageOnly = async () => {
    if (!imageFile) {
      showSnackbar('Choose an image file first', 'warning');
      return;
    }
    setUploadingImage(true);
    try {
      const res = await uploadDiscoverSectionImage(imageFile);
      if (handleSessionExpired(res)) return;
      if (!isDiscoverSectionsApiSuccess(res)) {
        showSnackbar(res?.message || 'Failed to upload image', 'error');
        return;
      }
      const uploadedImageUrl = getDiscoverSectionsPayload(res)?.imageUrl;
      if (!uploadedImageUrl) {
        showSnackbar('Upload finished but image URL was missing', 'error');
        return;
      }
      setFormData((prev) => ({ ...prev, imageUrl: uploadedImageUrl }));
      setImageFile(null);
      showSnackbar('Image uploaded');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleToggleEnabled = async (section, enabled) => {
    if (!enabled && !window.confirm('Disable this discover section? It will disappear from /discover when inactive.')) {
      return;
    }
    setTogglingId(section.id);
    try {
      const res = await setDiscoverSectionEnabled(section.id, enabled);
      if (handleSessionExpired(res)) return;
      if (!isDiscoverSectionsApiSuccess(res)) {
        showSnackbar(res?.message || 'Failed to update enabled status', 'error');
        return;
      }
      showSnackbar(enabled ? 'Discover section enabled' : 'Discover section disabled');
      loadSections();
    } finally {
      setTogglingId(null);
    }
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    setPreviewData(null);
    try {
      const res = await previewDiscover({ competitionCid: previewCompetitionCid.trim() || undefined });
      if (handleSessionExpired(res)) return;
      if (!isDiscoverSectionsApiSuccess(res)) {
        showSnackbar(res?.message || 'Failed to preview discover response', 'error');
        return;
      }
      setPreviewData(getDiscoverSectionsPayload(res));
      showSnackbar('Discover preview loaded');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleVideoSelectionChange = (_, newValue) => {
    setSelectedVideoIds(newValue.map((asset) => asset.id));
  };

  const pageContent = (
    <>
      {!embedded && (
        <div className="container-fluid">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-6">
                <h3>Discover Sections</h3>
                <Typography variant="body2" color="text.secondary">
                  Manage Discover cards, titles, images, tap destinations, videos, and competition overrides.
                </Typography>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={embedded ? undefined : 'container-fluid'}>
        <div className={embedded ? undefined : 'row'}>
          <div className={embedded ? undefined : 'col-sm-12'}>
            <div className={embedded ? undefined : 'card'}>
              <div className={embedded ? undefined : 'card-body'}>
                <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  {!embedded && (
                    <Button component={Link} to="/dashboard" startIcon={<ArrowBackIcon />} variant="outlined" size="small">
                      Back
                    </Button>
                  )}
                  <Button variant="contained" startIcon={<Add />} onClick={handleOpenCreate}>
                    Add section
                  </Button>
                  <TextField
                    size="small"
                    label="Competition CID filter"
                    value={competitionFilter}
                    onChange={(event) => setCompetitionFilter(event.target.value)}
                    sx={{ minWidth: 220 }}
                  />
                  <Button variant="text" onClick={loadSections} disabled={loading}>
                    Refresh
                  </Button>
                </Box>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography color="text.secondary" variant="body2">Sections loaded</Typography>
                        <Typography variant="h5" sx={{ mt: 1 }}>{stats.total}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography color="text.secondary" variant="body2">Enabled</Typography>
                        <Typography variant="h5" sx={{ mt: 1 }}>{stats.enabled}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography color="text.secondary" variant="body2">Competition overrides</Typography>
                        <Typography variant="h5" sx={{ mt: 1 }}>{stats.competitionOverrides}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <Card variant="outlined" sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 1 }}>Preview Public Discover Cards</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
                      <TextField
                        size="small"
                        label="Competition CID"
                        value={previewCompetitionCid}
                        onChange={(event) => setPreviewCompetitionCid(event.target.value)}
                        sx={{ minWidth: 220 }}
                      />
                      <Button variant="contained" onClick={handlePreview} disabled={previewLoading}>
                        {previewLoading ? 'Previewing...' : 'Preview /discover'}
                      </Button>
                    </Box>
                    {previewLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : previewData ? (
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa', maxHeight: 220, overflow: 'auto' }}>
                        <Typography component="pre" variant="caption" sx={{ m: 0, whiteSpace: 'pre-wrap' }}>
                          {JSON.stringify(previewData, null, 2)}
                        </Typography>
                      </Paper>
                    ) : (
                      <Alert severity="info">
                        Preview shows which card rows the mobile `/discover` API will choose for the competition.
                      </Alert>
                    )}
                  </CardContent>
                </Card>

                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Section</TableCell>
                          <TableCell>Title</TableCell>
                          <TableCell>Image</TableCell>
                          <TableCell>Direction URL</TableCell>
                          <TableCell>Enabled</TableCell>
                          <TableCell>Competition</TableCell>
                          <TableCell>Videos (live)</TableCell>
                          <TableCell>Sort</TableCell>
                          <TableCell>Active window</TableCell>
                          <TableCell>Updated</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sections.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={12} align="center">
                              No discover sections found
                            </TableCell>
                          </TableRow>
                        ) : (
                          sections.map((section) => {
                            const sectionCode = getValue(section, 'sectionCode', 'section_code');
                            const imageUrl = getValue(section, 'imageUrl', 'image_url');
                            const startsAt = getValue(section, 'startsAt', 'starts_at');
                            const endsAt = getValue(section, 'endsAt', 'ends_at');
                            return (
                              <TableRow key={section.id} hover>
                                <TableCell>{section.id}</TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={SECTION_CODE_OPTIONS.find((option) => option.value === sectionCode)?.label || sectionCode}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" fontWeight={500}>
                                    {getValue(section, 'title', 'title')}
                                  </Typography>
                                  {sectionCode === 'fan_stories' && (
                                    <Typography variant="caption" color="text.secondary">
                                      competitionCid controls IPL recap override
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {imageUrl ? (
                                    <img
                                      src={imageUrl}
                                      alt={getValue(section, 'title', 'title') || ''}
                                      style={{ width: 64, height: 44, objectFit: 'cover', borderRadius: 6 }}
                                    />
                                  ) : (
                                    '-'
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      maxWidth: 160,
                                      display: 'block',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                    title={getDirectionUrl(section) || ''}
                                  >
                                    {getDirectionUrl(section) || '-'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Switch
                                    size="small"
                                    checked={Boolean(getValue(section, 'enabled', 'enabled'))}
                                    disabled={togglingId === section.id}
                                    onChange={(event) => handleToggleEnabled(section, event.target.checked)}
                                  />
                                </TableCell>
                                <TableCell>{getValue(section, 'competitionCid', 'competition_cid') ?? 'Global'}</TableCell>
                                <TableCell>
                                  {(() => {
                                    const summary = formatAssignedVideosSummary(section);
                                    if (!summary) return '-';
                                    if (summary.total === 0) {
                                      return (
                                        <Typography variant="caption" color="text.secondary">
                                          None
                                        </Typography>
                                      );
                                    }
                                    return (
                                      <Box>
                                        <Typography
                                          variant="body2"
                                          fontWeight={500}
                                          color={summary.liveCount < summary.total ? 'warning.main' : 'text.primary'}
                                        >
                                          {summary.label}
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                          {summary.videos.length > 0
                                            ? summary.videos.map((video) => {
                                                const status = getScheduleStatus(video);
                                                return (
                                                  <Chip
                                                    key={video.id}
                                                    size="small"
                                                    color={SCHEDULE_STATUS_COLORS[status] || 'default'}
                                                    label={`#${video.id} ${status}`}
                                                    variant={status === 'live' ? 'filled' : 'outlined'}
                                                  />
                                                );
                                              })
                                            : getAssignedVideoIds(section).map((id) => (
                                                <Chip key={id} size="small" label={`#${id}`} variant="outlined" />
                                              ))}
                                        </Box>
                                      </Box>
                                    );
                                  })()}
                                </TableCell>
                                <TableCell>{getValue(section, 'sortOrder', 'sort_order') ?? 0}</TableCell>
                                <TableCell>
                                  <Typography variant="caption" display="block">Start: {formatDateTime(startsAt)}</Typography>
                                  <Typography variant="caption" display="block">End: {formatDateTime(endsAt)}</Typography>
                                </TableCell>
                                <TableCell>{formatDateTime(getValue(section, 'updatedAt', 'updated_at'))}</TableCell>
                                <TableCell align="right">
                                  <IconButton color="primary" size="small" onClick={() => handleOpenEdit(section)}>
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingSection ? `Edit Discover Section #${editingSection.id}` : 'Add Discover Section'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Section code"
                value={formData.sectionCode}
                onChange={(event) => updateForm('sectionCode', event.target.value)}
              >
                {SECTION_CODE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Competition CID"
                value={formData.competitionCid}
                onChange={(event) => updateForm('competitionCid', event.target.value)}
                helperText="Leave empty for global row"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Title"
                value={formData.title}
                onChange={(event) => updateForm('title', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Sort order"
                type="number"
                value={formData.sortOrder}
                onChange={(event) => updateForm('sortOrder', event.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required={!imageFile}
                label="Image URL"
                value={formData.imageUrl}
                onChange={(event) => updateForm('imageUrl', event.target.value)}
                helperText="Use an existing URL or upload an image below. Uploaded image wins on save."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Direction URL"
                value={formData.directionUrl}
                onChange={(event) => updateForm('directionUrl', event.target.value)}
                placeholder="/missions   or   dugout://arcade   or   https://dugoutlive.com/…"
                helperText="In-app route, deeplink, or https URL. Empty = default story viewer."
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button variant="outlined" component="label">
                  Choose image
                  <input
                    hidden
                    accept="image/*"
                    type="file"
                    onChange={(event) => setImageFile(event.target.files?.[0] || null)}
                  />
                </Button>
                <Button variant="text" onClick={handleUploadImageOnly} disabled={!imageFile || uploadingImage}>
                  {uploadingImage ? 'Uploading...' : 'Upload image only'}
                </Button>
                <Typography variant="body2" color="text.secondary">
                  {imageFile ? imageFile.name : 'No image selected'}
                </Typography>
              </Box>
            </Grid>
            {isVideoBackedSection(formData.sectionCode) && (
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={selectableGlobalVideos}
                  value={selectedVideoOptions}
                  onChange={handleVideoSelectionChange}
                  getOptionLabel={(option) => {
                    const title = getAssetValue(option, 'title', 'title');
                    const type = getAssetType(option);
                    return title ? `#${option.id} — ${title} (${type})` : `#${option.id} (${type})`;
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const assigned = editingAssignedVideosById.get(option.id);
                      const status = assigned ? getScheduleStatus(assigned) : null;
                      const title = getAssetValue(option, 'title', 'title');
                      const label = status
                        ? `#${option.id}${title ? ` ${title}` : ''} · ${status}`
                        : `#${option.id}${title ? ` — ${title}` : ''}`;
                      return (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.id}
                          size="small"
                          color={status ? (SCHEDULE_STATUS_COLORS[status] || 'default') : 'default'}
                          label={label}
                        />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Assigned global videos (multi-select)"
                      helperText="Any promotional / dugout_daily / announcement asset. Save sends videoIds + makeLive:true. Empty = items: []. Prefer empty Fan Video start/end dates."
                    />
                  )}
                />
                {selectedVideoIds.length > 0 && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    {selectedVideoIds.length} video(s) selected. Assigned + enabled videos play as multiple story slides.
                  </Alert>
                )}
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Starts at"
                type="datetime-local"
                value={formData.startsAt}
                onChange={(event) => updateForm('startsAt', event.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Prefer scheduling the tile here instead of per-video dates"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Ends at"
                type="datetime-local"
                value={formData.endsAt}
                onChange={(event) => updateForm('endsAt', event.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Tile active window gates Discover visibility"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={4}
                label="Metadata JSON"
                value={formData.metadata}
                onChange={(event) => updateForm('metadata', event.target.value)}
              />
            </Grid>
            {formData.sectionCode === 'fan_stories' && (
              <Grid item xs={12}>
                <Alert severity="info">
                  For fan stories, `competitionCid` controls competition-specific labels/images, such as IPL Recap.
                </Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enabled}
                    onChange={(event) => updateForm('enabled', event.target.checked)}
                  />
                }
                label="Enabled in Discover"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );

  if (embedded) {
    return pageContent;
  }

  return (
    <div>
      <Component>
        <div className="page-body">{pageContent}</div>
      </Component>
    </div>
  );
}

export default DiscoverSections;
